import {
  useCallback,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { getRequest } from "../../../helpers/api.helper";
import {
  AppDispatch,
  RootState,
} from "../../../store";
import { setDashboard } from "../../../store/dashboard.slice";

import { BureauDashboardData } from "../dashboard.types";
import { parseNumericValue } from "../dashboard.utils";

type UseDashboardDataOptions = {
  onPayloadLoaded: (
    payload: BureauDashboardData,
  ) => void;
};

const extractDashboardPayload = (
  raw: unknown,
): BureauDashboardData | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const value = raw as Record<string, any>;

  if (value.dashboard) {
    return extractDashboardPayload(value.dashboard);
  }

  if (
    value.data &&
    typeof value.data === "object"
  ) {
    const nested = extractDashboardPayload(
      value.data,
    );

    if (nested) {
      return nested;
    }
  }

  return value as BureauDashboardData;
};

const useDashboardData = ({
  onPayloadLoaded,
}: UseDashboardDataOptions) => {
  const dispatch = useDispatch<AppDispatch>();

  const dashboardStore = useSelector(
    (state: RootState) => state.dashboard,
  );

  const dashboardData =
    dashboardStore.data as BureauDashboardData | null;

  const [dashboardLoading, setDashboardLoading] =
    useState(() => !dashboardStore.loaded);

  const [dashboardError, setDashboardError] =
    useState<string | null>(null);

  const [leadCounts, setLeadCounts] = useState({
    whatsappClicks: 0,
    callClicks: 0,
  });

  const applyPayload = useCallback(
    (payload: BureauDashboardData) => {
      onPayloadLoaded(payload);

      setLeadCounts({
        whatsappClicks:
          parseNumericValue(
            payload?.meta?.whatsappClicks ??
              payload?.whatsappClicks ??
              0,
          ) ?? 0,

        callClicks:
          parseNumericValue(
            payload?.meta?.callClicks ??
              payload?.callClicks ??
              0,
          ) ?? 0,
      });
    },
    [onPayloadLoaded],
  );

  const fetchDashboardData = useCallback(
    async (options?: { manual?: boolean }) => {
      const manual = options?.manual ?? false;

      setDashboardLoading(true);
      setDashboardError(null);

      try {
        const response = await getRequest(
          "/bureau/dashboard",
        );

        if (!response?.success) {
          throw new Error(
            response?.message ||
              "Failed to load dashboard data.",
          );
        }

        const payload =
          extractDashboardPayload(response.data);

        if (!payload) {
          throw new Error(
            "Invalid dashboard response.",
          );
        }

        dispatch(setDashboard(payload));
        applyPayload(payload);

        if (manual) {
          toast.success("Dashboard refreshed.");
        }

        return payload;
      } catch (error: any) {
        console.error(
          "Error fetching bureau dashboard data:",
          error,
        );

        const message =
          error?.message ||
          "Unable to load dashboard data. Please try again shortly.";

        setDashboardError(message);

        if (manual) {
          toast.error(
            "Unable to refresh dashboard.",
          );
        }

        return null;
      } finally {
        setDashboardLoading(false);
      }
    },
    [applyPayload, dispatch],
  );

  const refreshDashboard = useCallback(async () => {
    if (dashboardLoading) {
      return;
    }

    await fetchDashboardData({
      manual: true,
    });
  }, [dashboardLoading, fetchDashboardData]);

  useEffect(() => {
    if (
      dashboardStore.loaded &&
      dashboardData
    ) {
      applyPayload(dashboardData);
      return;
    }

    void fetchDashboardData();
  }, [
    dashboardStore.loaded,
    dashboardData,
    applyPayload,
    fetchDashboardData,
  ]);

  return {
    dashboardData,
    dashboardLoading,
    dashboardError,
    leadCounts,
    fetchDashboardData,
    refreshDashboard,
  };
};

export default useDashboardData;
